import {Injectable} from '@angular/core';
import {EditorRegistryService} from '../../diagram-render/mx-graph/editor/editor-registry.service';
import {GraphHelperService} from '../../diagram-render/helpers/graph-helper.service';
import {DiagramCanvasService} from '../../diagram-render/diagram-canvas/diagram-canvas.service';
import {AuthService} from '../../core/auth/auth.service';
import {NavigationService} from '../../core/navigation/navigation.service';
import {DiagramJsonHandlerService} from '../../diagram-data/diagram-json-handler.service';
import {FirebaseDataService} from '../../editor-state/firebase-data.service';
import {ShapeSpawnerService} from '../../diagram-render/shapes/shape-spawner.service';
import {DIAGRAM_VERSION_STATES_ENUM} from '../../editor-interface/shared/models/diagram-version.constant';
import {PopupManagerService} from '../../core/services/popup-manager.service';
import {ModeDataService} from '../../editor-state/mode-data.service';
import {ShapesTemplateService} from 'src/app/diagram-render/shapes/shapes-template.service';

declare let mxGeometry: any;
declare let mxUtils: any;

@Injectable()
export class StickersService {
  layerVisible = true;
  hiddenProps = {author: {}, paperClass: {}, tags: {}};

  constructor(
    private editorRegistry: EditorRegistryService,
    private graphHelper: GraphHelperService,
    private navSvc: NavigationService,
    private authService: AuthService,
    private diagramJsonSvc: DiagramJsonHandlerService,
    private canvasSvc: DiagramCanvasService,
    private shapeSpawnerSvc: ShapeSpawnerService,
    private popupSvc: PopupManagerService,
    private modeDataSvc: ModeDataService,
    fbDataSvc: FirebaseDataService,
    private templateSvc: ShapesTemplateService
  ) {
    fbDataSvc.addService<StickersService>('stickersSvc', this);
    shapeSpawnerSvc.addService<StickersService>('stickersSvc', this);
  }

  get isLayerVisible() {
    return this.layerVisible;
  }

  get listHiddenProps() {
    return this.hiddenProps;
  }

  isSameAuthor(stickerCell) {
    if (!stickerCell) {
      return false;
    }

    const stickerJson = stickerCell.getJson() || {};
    const {id} = this.authService.getUserDataSync();
    const {author} = stickerJson;

    return author?.id && String(id) === String(author?.id);
  }

  canForceEdit(cell) {
    const isDraft = this.diagramJsonSvc.get()?.state === DIAGRAM_VERSION_STATES_ENUM.DRAFT;
    const isSameAuthor = this.isSameAuthor(cell);
    const canAddNotes = this.authService.getCanAddNotes();

    return isDraft && isSameAuthor && canAddNotes;
  }

  forceStartEditing(cell, evt) {
    const graph = this.editorRegistry.getEditorUI().editor.graph;

    if (this.canForceEdit(cell)) {
      graph.startEditingAtCell(cell, evt);
    }
  }

  updateExisting(data) {
    const graph = this.editorRegistry.getGraph();
    const {id} = this.authService.getUserDataSync();
    const {editor} = data;
    const isSameUser = editor && String(id) === editor.id;

    if (!graph || !graph.isEnabled() || this.navSvc.isNavActive() || isSameUser) {
      return;
    }

    const model = graph.getModel();
    const cell = model.getCell(data.mxKey);

    if (!cell) {
      return;
    }

    if (graph.isEditing(cell)) {
      this.diagramJsonSvc.update({changeNote: data});

      return;
    }

    const p = data.position;
    const newJson = {...cell.getJson(), tags: data.tags || [], text: data.text};
    const newValue = this.templateSvc.sticker(newJson);
    const newGeo = new mxGeometry(p.x, p.y, p.w, p.h);

    cell.skipUpdate = true;
    model.beginUpdate();
    model.setValue(cell, newValue);
    model.setGeometry(cell, newGeo);
    model.setStyle(cell, mxUtils.setStyle(cell.style, 'rotation', p.rotation));
    model.setStyle(cell, mxUtils.setStyle(cell.style, 'paperClass', data.paperClass));
    model.endUpdate();

    this.diagramJsonSvc.update({changeNote: data});
  }

  createExisting(data) {
    const {id} = this.authService.getUserDataSync();
    const {editor} = data;
    const isSameUser = editor && String(id) === editor.id;
    const graph = this.editorRegistry.getGraph();

    if (!graph || !graph.isEnabled() || this.navSvc.isNavActive() || isSameUser) {
      return;
    }

    if (graph.model.getCell(data.mxKey)) {
      return;
    }

    const stickerCell = this.shapeSpawnerSvc.createSticker(data);

    graph.addCell(stickerCell, this.graphHelper.stickerParent());

    this.diagramJsonSvc.update({createNote: data});
  }

  checkAndRemoveExisting(serverStickers) {
    const graph = this.editorRegistry.getGraph();
    const allStickers = this.graphHelper.allStickers();

    if (!graph || !graph.isEnabled() || this.navSvc.isNavActive()) {
      return;
    }

    const stickersMap = serverStickers.reduce((acc, curr) => {
      if (!acc[Number(curr.mxKey)]) {
        acc[Number(curr.mxKey)] = curr;
      }

      return acc;
    }, {});
    const cellsToRemove = [];

    allStickers.forEach(sticker => {
      const mxKey = Number(sticker.id);

      if (!stickersMap[mxKey] && mxKey < 0) {
        this.diagramJsonSvc.update({deleteNote: {mxKey}});
        cellsToRemove.push(sticker);
      }
    });
    graph.removeCells(cellsToRemove, false);
  }

  switchVisible(resetFilters = false, force?) {
    if (resetFilters) {
      this.filterNotes({author: {}, paperClass: {}, tags: {}});
    }

    if (typeof force !== 'undefined') {
      this.layerVisible = force;
    } else {
      this.layerVisible = !this.layerVisible;

      if (!this.layerVisible) {
        this.popupSvc.filterNotesDialogRef?.close();
      }
    }

    this.canvasSvc.stickersVisible = this.isLayerVisible;
    const graph = this.editorRegistry.getEditorUI().editor.graph;
    const model = graph.model;

    model.setVisible(this.graphHelper.stickerParent(), this.layerVisible);
  }

  get countStickers() {
    return this.graphHelper.allStickers().length;
  }

  filterNotes(hiddenProps) {
    this.hiddenProps = hiddenProps;
    const jsons = this.diagramJsonSvc.get()?.notes || [];
    const graph = this.editorRegistry.getEditorUI().editor.graph;
    const model = graph.model;

    jsons.forEach(json => {
      const cell = model.getCell(json.mxKey);
      const visible = this.isNoteVisible(json.author?.id, json.paperClass, json.tags);

      model.setVisible(cell, visible);
    });
  }

  public isNoteVisible(authorId, paperClass, tags) {
    return (
      !this.hiddenProps.author[authorId] &&
      this.isNoteColorVisible(paperClass) &&
      this.isNoteTagsVisible(tags)
    );
  }

  public isNoteColorVisible(paperClass) {
    return !this.hiddenProps.paperClass[paperClass];
  }

  public isNoteTagsVisible(tags) {
    if (!tags?.length) {
      return true;
    }

    return !tags?.every(tag => this.hiddenProps.tags[tag]);
  }
}
